import { SelectedProduct } from '../../../../types/sales.types';
import { formatPriceAdjustmentDisplay } from '../../../../utils/formatters/money';
import { roundMoney } from '../../../../utils/money';
import { calculateLineDiscountAmountOnAdjusted } from '../../../../utils/sales/line-pricing.utils';

export function hasProductDiscount(product: SelectedProduct): boolean {
  return product.discountType !== 'NONE' && Number(product.discountValue) > 0;
}

export function getProductLineDiscountAmount(product: SelectedProduct): number {
  return calculateLineDiscountAmountOnAdjusted(
    product.adjustedPrice,
    product.discountType,
    product.discountValue,
  );
}

/** Effective discount amount after exchange pricing rules. */
export function getEffectiveLineDiscountAmount(originalPrice: number, finalPrice: number): number {
  return roundMoney(Math.max(0, roundMoney(originalPrice) - roundMoney(finalPrice)));
}

export function getEffectiveLineDiscountPercentage(
  originalPrice: number,
  finalPrice: number,
): number {
  const price = roundMoney(originalPrice);
  if (price <= 0) return 0;
  const amount = getEffectiveLineDiscountAmount(price, finalPrice);
  return roundMoney(Math.max(0, Math.min(99, (amount / price) * 100)));
}

export function formatExchangeDiscountDisplay(
  _formatCurrency: (amount: number) => string,
  product: SelectedProduct,
  finalPrice: number,
): string {
  const originalPrice = roundMoney(product.originalPrice);
  const amount = getEffectiveLineDiscountAmount(originalPrice, finalPrice);
  if (amount <= 0) {
    return '—';
  }

  const type = product.discountType === 'PERCENTAGE' ? 'PERCENTAGE' : 'AMOUNT';
  const value =
    type === 'PERCENTAGE' ? getEffectiveLineDiscountPercentage(originalPrice, finalPrice) : amount;
  return formatPriceAdjustmentDisplay(type, value, originalPrice);
}

export function formatExchangeDiscountLineLabel(
  product: SelectedProduct,
  finalPrice: number,
): string {
  const originalPrice = roundMoney(product.originalPrice);
  const amount = getEffectiveLineDiscountAmount(originalPrice, finalPrice);
  if (amount <= 0) {
    return 'Sin descuento';
  }

  if (product.discountType === 'PERCENTAGE') {
    return `${getEffectiveLineDiscountPercentage(originalPrice, finalPrice)}%`;
  }

  return 'Monto fijo';
}

/** Sum of per-line discount amounts for new exchange products. */
export function computeTotalDiscountAmount(products: SelectedProduct[]): number {
  return products.reduce((total, product) => total + getProductLineDiscountAmount(product), 0);
}

export function formatDiscountDisplay(
  _formatCurrency: (amount: number) => string,
  discountType: string,
  discountValue: number,
  originalPrice: number,
): string {
  return formatPriceAdjustmentDisplay(discountType, discountValue, originalPrice);
}

export function formatDiscountLineLabel(
  _formatCurrency: (amount: number) => string,
  product: SelectedProduct,
): string {
  if (product.discountType === 'PERCENTAGE') {
    return `${product.discountValue}%`;
  }
  return 'Monto fijo';
}
